<script lang="ts" setup>
const { getQueryAttributes } = useApiAttributes()
const { refreshResponseWithApiVersion } = useApiDoc()
const { segmentation } = useSegmentation()
const { t } = useI18n()
const api = useApi()

const { data: response, execute, status } = useAsyncData(
  'doc:companies-count',
  () => api.companies.count(),
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/companies/count',
  },
  {
    method: 'POST',
    path: '/companies/count',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    attributes: getQueryAttributes(),
    description: t('attributes.query'),
    endpoint: '/companies/count',
    hasSegmentation: true,
    name: 'query',
    required: true,
    type: 'string',
    warning: t('globals.stringifyAndEncode'),
  },
  {
    description: t('attributes.search'),
    example: '/companies/count?search=amazon',
    name: 'search',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.searchFields'),
    example: '/companies/count?searchFields[]=domain&searchFields[]=name',
    name: 'searchFields',
    optional: true,
    type: 'array',
    values: segmentation.attributesWeCanSearch,
  },
]

const responseAttributes: ApiAttribute[] = [
  {
    description: t('response.count'),
    name: 'count',
    type: 'number',
  },
]

async function onVisibilityChange(visibility: boolean) {
  refreshResponseWithApiVersion({
    api,
    execute,
    response,
    visibility,
  })
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "query": "An array of conditions the companies must match.",
      "search": "An additional search query applied to the company name or domain.",
      "searchFields": "Define the fields to search."
    },
    "response": {
      "count": "The number of companies matching the conditions."
    }
  },
  "es": {
    "attributes": {
      "query": "Un array de condiciones que las empresas deben cumplir.",
      "search": "Una consulta de búsqueda adicional aplicada al nombre de la empresa o al dominio.",
      "searchFields": "Defina los campos para buscar."
    },
    "response": {
      "count": "El número de empresas que coinciden con las condiciones."
    }
  },
  "fr": {
    "attributes": {
      "query": "Un array de conditions que les entreprises doivent remplir.",
      "search": "Une requête de recherche supplémentaire appliquée au nom de l'entreprise ou au domaine.",
      "searchFields": "Définissez les champs à rechercher."
    },
    "response": {
      "count": "Le nombre d'entreprises correspondant aux conditions."
    }
  }
}
</i18n>
